import { type BaseEvent, type EventProducer } from '@atlassian/state-machine/types';

import { WsSocketError } from '../../../../network/errors';
import { type CollabConnectionClientSender } from '../../../../transport/collab-connection-transport';
import { type SocketMachineContext } from '../../../network-state-machine-context';
import { type Events } from '../../../network-state-machine-events';

export type ConnectingContext = Pick<
  SocketMachineContext,
  | 'accessToken'
  | 'relayDetails'
  | 'remoteDocument'
  | 'sessionId'
  | 'transport'
  | 'getAnalyticsWebClient'
  | 'stateBridge'
  | 'traceId'
  | 'signal'
  | 'accessTokenSupplier'
  | 'relaySupplier'
  | 'debounce'
  | 'onAckTimeout'
>;

export function asyncProducer<T extends BaseEvent>(
  ctx: { signal: AbortSignal },
  events: EventProducer<T>
) {
  return (event: T) => {
    if (!ctx.signal.aborted) {
      events(event);
    }
  };
}

export function onEnterWebSocket(ctx: ConnectingContext, events: EventProducer<Events>) {
  const producer = asyncProducer(ctx, events);

  if (!ctx.relayDetails) {
    producer({
      type: 'error',
      error: new WsSocketError(new Error('Missing relay details in context')),
    });
    return;
  }

  if (!ctx.remoteDocument) {
    producer({
      type: 'error',
      error: new WsSocketError(new Error('Missing remote document in context')),
    });
    return;
  }

  if (!ctx.accessToken) {
    producer({
      type: 'error',
      error: new WsSocketError(new Error('Missing access token in context')),
    });
    return;
  }

  try {
    const { collabStrategy } = ctx.remoteDocument;

    const transport = collabStrategy.createTransport(
      {
        accessToken: ctx.accessToken,
        relayDetails: ctx.relayDetails,
        traceId: ctx.traceId,
        sessionId: ctx.sessionId,
        signal: ctx.signal,
        collabStrategy,
        accessTokenSupplier: ctx.accessTokenSupplier,
        relaySupplier: ctx.relaySupplier,
        connectionAttempt: ctx.debounce.attempt,
        getAnalyticsWebClient: ctx.getAnalyticsWebClient,
        stateBridge: ctx.stateBridge,
      },
      {
        onOpen: () => {
          producer({ type: 'open' });
        },
        onClose: (code: number, reason: string) => {
          producer({
            type: 'close',
            error: new WsSocketError({ code, reason } as CloseEvent),
            code,
          });
        },
        onError: (error: Error) => {
          const wsError =
            error instanceof WsSocketError ? error : new WsSocketError(error);
          producer({ type: 'error', error: wsError });
        },
        onMessage: (
          data: Uint8Array<ArrayBuffer>,
          sender?: CollabConnectionClientSender
        ) => {
          producer({ type: 'messageReceived', message: data, sender });
        },
        onTransientMessage: (data: Uint8Array<ArrayBuffer>) => {
          producer({ type: 'transientMessageReceived', message: data });
        },
        onAckTimeout: ctx.onAckTimeout,
      }
    );

    ctx.transport = transport;
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (error) {
    producer({
      type: 'error',
      error: new WsSocketError(error instanceof Error ? error : new Error(String(error))),
    });
  }
}
